import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Download, RefreshCcw, AlertTriangle } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { apiFetchJson } from '../lib/api';
import { exportCsv } from '../lib/csv';
import { formatPrice } from '../lib/utils';

// 客戶四分群（客人 2026-09-05 業務規則第三條）：後台精準分群營銷用。
// 資料分開留存、互不覆蓋：訪問 IP 地區（visit_logs）× 訂單收貨地址地區（order_records.region）。
// 重點：反差A／反差B＝IP 所在地與歷史收貨地址不一致的用戶。

interface CustomerGroupRow {
  phone: string;
  name: string | null;
  total_orders: number;
  overseas_orders: number;
  local_orders: number;
  total_spent: number;
  last_order_at: number;
  visits: number;
  local_visits: number;
  overseas_visits: number;
  ip_majority: 'hk' | 'overseas' | 'unknown';
  group: 'pure_local' | 'pure_overseas' | 'mismatch_a' | 'mismatch_b' | 'unknown';
}

const GROUP_TABS: { value: string; label: string }[] = [
  { value: '', label: '全部' },
  { value: 'pure_local', label: '純本地' },
  { value: 'pure_overseas', label: '純海外' },
  { value: 'mismatch_a', label: '反差A（IP本地→寄海外）' },
  { value: 'mismatch_b', label: '反差B（IP境外→寄香港）' },
  { value: 'unknown', label: 'IP資料不足' },
];

const GROUP_LABELS: Record<string, string> = {
  pure_local: '純本地',
  pure_overseas: '純海外',
  mismatch_a: '反差A',
  mismatch_b: '反差B',
  unknown: '資料不足',
};

const GROUP_STYLES: Record<string, string> = {
  pure_local: 'bg-green-100 text-green-800',
  pure_overseas: 'bg-blue-100 text-blue-800',
  mismatch_a: 'bg-amber-100 text-amber-800',
  mismatch_b: 'bg-purple-100 text-purple-800',
  unknown: 'bg-gray-100 text-gray-600',
};

function fmtDate(unixSeconds: number | null | undefined): string {
  if (!unixSeconds) return '—';
  const d = new Date(unixSeconds * 1000);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function fmtPhone(phone: string): string {
  return phone.startsWith('852') && phone.length === 11 ? `+852 ${phone.slice(3)}` : `+${phone}`;
}

export default function AdminCustomerGroups() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<CustomerGroupRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [groupTab, setGroupTab] = useState('');
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiFetchJson<CustomerGroupRow[] | { data?: CustomerGroupRow[] }>('/public/admin/customer-groups');
      const list = (Array.isArray(res) ? res : res?.data || []) as CustomerGroupRow[];
      setRows(list);
    } catch {
      setError('載入失敗，請稍後再試');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    if (confirm('確定要登出嗎？')) {
      localStorage.removeItem('admin_token');
      navigate('/admin');
    }
  };

  const searchLower = search.trim().toLowerCase();
  const filtered = rows.filter((r) => {
    const groupMatch = !groupTab || r.group === groupTab;
    const searchMatch = !searchLower
      || (r.phone || '').includes(searchLower)
      || (r.name || '').toLowerCase().includes(searchLower);
    return groupMatch && searchMatch;
  });

  const counts: Record<string, number> = { '': rows.length };
  for (const r of rows) counts[r.group] = (counts[r.group] || 0) + 1;

  const handleExportCsv = () => {
    exportCsv(
      `客戶四分群_${new Date().toISOString().slice(0, 10)}`,
      ['電話', '姓名', '分群', 'IP多數地區', '到訪次數', '本地到訪', '海外到訪', '總訂單', '本地訂單', '海外訂單', '累計消費', '最近下單'],
      filtered.map((r) => [
        fmtPhone(r.phone),
        r.name || '',
        GROUP_LABELS[r.group] || r.group,
        r.ip_majority === 'hk' ? '香港' : r.ip_majority === 'overseas' ? '海外' : '資料不足',
        r.visits,
        r.local_visits,
        r.overseas_visits,
        r.total_orders,
        r.local_orders,
        r.overseas_orders,
        (r.total_spent / 100).toFixed(2),
        fmtDate(r.last_order_at),
      ])
    );
  };

  return (
    <AdminLayout currentPage="customer-groups" onLogout={handleLogout}>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Users size={24} /> 客戶四分群
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            IP 所在地（訪問日誌）× 收貨地址地區（訂單數據）分開留存、互不覆蓋——專找反差用戶做精準分群營銷。
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={load}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 text-sm hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <RefreshCcw size={16} /> 重新整理
          </button>
          <button
            onClick={handleExportCsv}
            disabled={filtered.length === 0}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-x1493-primary text-white text-sm font-bold hover:bg-red-700 disabled:opacity-40"
          >
            <Download size={16} /> 匯出 CSV
          </button>
        </div>
      </div>

      {/* 分群頁籤 */}
      <div className="mb-4 bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="flex flex-wrap gap-2">
          {GROUP_TABS.map((t) => (
            <button
              key={t.value}
              onClick={() => setGroupTab(t.value)}
              className={`px-3 py-2 rounded-xl text-sm font-medium transition ${
                groupTab === t.value
                  ? 'bg-x1493-primary text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {t.label}
              <span className="ml-1.5 text-xs opacity-75">({counts[t.value] || 0})</span>
            </button>
          ))}
        </div>
        <div className="mt-3">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="搜尋電話／姓名…"
            className="p-2 border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-xl text-sm w-full sm:w-64 focus:ring-2 focus:ring-x1493-primary outline-none"
          />
        </div>
      </div>

      {/* 反差用戶提示 */}
      {(groupTab === 'mismatch_a' || groupTab === 'mismatch_b') && (
        <div className="mb-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-900 dark:text-amber-200 p-4 rounded-xl text-sm flex items-start gap-2">
          <AlertTriangle size={18} className="mt-0.5 flex-shrink-0" />
          <span>
            反差用戶＝IP 所在地與收貨地址不一致：人在香港買貨寄海外親友（反差A）、或身在海外寄回香港（反差B）。
            適合推送「代寄親友」「海外直郵」定向優惠。
          </span>
        </div>
      )}

      {error && <div className="mb-4 bg-red-50 text-red-700 p-4 rounded-xl text-sm">{error}</div>}

      {loading ? (
        <div className="text-center py-20 text-gray-500">載入中...</div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-700 text-left">
              <tr>
                <th className="px-4 py-3 font-semibold text-gray-700 dark:text-gray-200">電話</th>
                <th className="px-4 py-3 font-semibold text-gray-700 dark:text-gray-200">姓名</th>
                <th className="px-4 py-3 font-semibold text-gray-700 dark:text-gray-200">分群</th>
                <th className="px-4 py-3 font-semibold text-gray-700 dark:text-gray-200">IP 多數</th>
                <th className="px-4 py-3 font-semibold text-gray-700 dark:text-gray-200">到訪（本/海）</th>
                <th className="px-4 py-3 font-semibold text-gray-700 dark:text-gray-200">訂單（本/海）</th>
                <th className="px-4 py-3 font-semibold text-gray-700 dark:text-gray-200">累計消費</th>
                <th className="px-4 py-3 font-semibold text-gray-700 dark:text-gray-200">最近下單</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-gray-500">
                    {rows.length === 0
                      ? '暫無數據——分群自新到訪（visit_logs）與訂單累積，上線後逐步建立'
                      : '沒有符合篩選條件的客戶'}
                  </td>
                </tr>
              ) : (
                filtered.map((r) => {
                  const isMismatch = r.group === 'mismatch_a' || r.group === 'mismatch_b';
                  return (
                    <tr
                      key={r.phone}
                      className={`border-t border-gray-100 dark:border-gray-700 ${
                        isMismatch ? 'bg-amber-50/60 dark:bg-amber-900/10' : ''
                      } hover:bg-gray-50 dark:hover:bg-gray-700/50`}
                    >
                      <td className="px-4 py-3 font-mono">{fmtPhone(r.phone)}</td>
                      <td className="px-4 py-3">{r.name || '—'}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-bold ${GROUP_STYLES[r.group] || ''}`}>
                          {GROUP_LABELS[r.group] || r.group}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {r.ip_majority === 'hk' ? '🇭🇰 香港' : r.ip_majority === 'overseas' ? '🌏 海外' : '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                        {r.visits > 0 ? `${r.local_visits} / ${r.overseas_visits}` : '—'}
                      </td>
                      <td className="px-4 py-3">
                        {r.total_orders}（{r.local_orders} / {r.overseas_orders}）
                      </td>
                      <td className="px-4 py-3 font-semibold">{formatPrice(r.total_spent)}</td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{fmtDate(r.last_order_at)}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
          <div className="px-4 py-3 text-xs text-gray-500 border-t border-gray-100 dark:border-gray-700">
            共 {filtered.length} 位客戶{groupTab || search ? '（已篩選）' : ''}· 到訪（本/海）＝visit_logs 經 visitor_id 橋接的本地/海外到訪次數
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
